import React, { useCallback, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import {
  Avatar,
  Code,
  flashRowProps,
  Progress,
  ProjectLabel,
  Table,
} from '../components/ui.jsx';
import { ProjectModal } from '../components/AdminModals.jsx';
import { ListToolbar, PageHeader } from '../components/PageSkeleton.jsx';
import { api } from '../lib/api.js';
import { PROJECT_TIERS } from '../lib/constants.js';
import { shapeWithChoices, sortOf } from '../lib/sessionState.js';
import { useSessionState } from '../lib/useSessionState.js';
import { DETAIL_TABS } from '../lib/urlState.js';
import { countActiveFilters } from '../lib/listFilters.js';
import { Select } from '../components/controls/Select.jsx';
import { ResetFiltersButton } from '../components/controls/ResetFiltersButton.jsx';
import { useProjectDetail } from './project/useProjectDetail.js';
import { ProjectTrackerTab } from './project/ProjectTrackerTab.jsx';
import { ProjectInfoTab } from './project/ProjectInfoTab.jsx';
import { ProjectHistoryTab } from './project/ProjectHistoryTab.jsx';
import { ProjectMemoryTab } from './project/ProjectMemoryTab.jsx';
import { nextMilestone } from './project/LifecycleTimeline.jsx';

// Labels for the tabs in `DETAIL_TABS.projects` (which also sets their order).
const PROJECT_TAB_LABELS = {
  info: 'Project Info',
  tracker: 'Effort Tracker',
  history: 'History',
  memory: 'Memory',
};

const PROJECT_FILTERS = {
  status: 'active',
  risk: false,
  client: 'all',
  person: 'all',
  tier: 'all',
  search: '',
};
const PROJECT_SORT = { key: 'project', direction: 'asc' };
const ALL_ONLY = ['all'];
const TIER_FILTER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  ...PROJECT_TIERS.map((tier) => ({
    value: String(tier),
    label: `Tier ${tier}`,
  })),
  { value: 'none', label: 'No tier' },
];
const PROJECT_FILTERS_SHAPE = shapeWithChoices({
  status: ['active', 'paused', 'completed', 'all'],
  tier: TIER_FILTER_OPTIONS.map((option) => option.value),
});

const STATUS_LABEL = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
};
const STATUS_CLASS = {
  active: 'status-active',
  paused: 'status-paused',
  completed: 'status-completed',
};
const hasBudget = (project) => project.budgetMD > 0;
const isAtRisk = (project) =>
  hasBudget(project) && project.spentMD / project.budgetMD > 0.85;
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const PROJECT_TABLE_HEADERS = [
  { label: 'Project', sortKey: 'project' },
  { label: 'Group', sortKey: 'group' },
  { label: 'Tier', sortKey: 'tier' },
  { label: 'Status', sortKey: 'status' },
  { label: 'Budget', sortKey: 'budget' },
  { label: 'Spent', sortKey: 'spent' },
  { label: 'Remaining', sortKey: 'remaining' },
  { label: 'Budget burn', sortKey: 'burn' },
  { label: 'Managers', sortKey: 'managers' },
];
const SORT_SHAPE = sortOf(
  PROJECT_TABLE_HEADERS.map((header) => header.sortKey),
);

// A project's "team" for filtering purposes spans every role it can carry —
// delivery/PM, account managers, support team, technical leads — so a single
// generic "person" filter answers "all projects for this person" without
// making the caller pick which role to search under.
const projectPeopleIds = (project) =>
  new Set([
    ...(project.managerIds || []),
    ...(project.accountManagerIds || []),
    ...(project.supportTeamIds || []),
    ...(project.technicalLeadIds || []),
  ]);

function projectSortValue(project, key, users) {
  if (key === 'project') return `${project.clientName} ${project.name}`;
  if (key === 'group') return project.groupName || '';
  // Unclassified projects sort after tier 3 ascending.
  if (key === 'tier') return project.tier ?? 4;
  if (key === 'status') return STATUS_LABEL[project.status] || project.status;
  if (key === 'budget') return project.budgetMD;
  if (key === 'burn')
    return hasBudget(project) ? project.spentMD / project.budgetMD : -1;
  if (key === 'spent') return project.spentMD;
  if (key === 'remaining')
    return hasBudget(project)
      ? project.budgetMD - project.spentMD
      : Number.NEGATIVE_INFINITY;
  if (key === 'managers') {
    return (project.managerIds || [])
      .map((id) => users.find((user) => user.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  }
  return '';
}

export function StatusBadge({ status }) {
  return (
    <span className={`project-status-badge ${STATUS_CLASS[status] || ''}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function Projects({ data, onOpen, onCreated }) {
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useSessionState(
    'projects.filters',
    PROJECT_FILTERS,
    PROJECT_FILTERS_SHAPE,
  );
  const [sort, setSort] = useSessionState(
    'projects.sort',
    PROJECT_SORT,
    SORT_SHAPE,
  );
  const [flashToken, setFlashToken] = useState(0);
  const setFilter = (key) => (value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const clientOptions = [...data.clients].sort((a, b) =>
    collator.compare(a.name, b.name),
  );
  const personOptions = [...data.users]
    .filter((user) => user.active)
    .sort((a, b) => collator.compare(a.name, b.name));

  // A remembered client or person that is no longer in the lists falls back
  // to "All" instead of filtering the list down to nothing.
  const clientFilter = clientOptions.some((c) => c.id === filters.client)
    ? filters.client
    : 'all';
  const personFilter = personOptions.some((u) => u.id === filters.person)
    ? filters.person
    : 'all';
  const statusFilter = filters.status;
  const riskOnly = filters.risk;
  const tierFilter = filters.tier;
  const search = filters.search;
  const activeCount = countActiveFilters(
    { ...filters, client: clientFilter, person: personFilter },
    PROJECT_FILTERS,
  );

  // The list runs entirely off the bootstrap payload (`data.allProjects`) — no
  // report query. Anything time-windowed (hours/MDs in a period, people, burn
  // over a range) lives in Reports; this view only answers "what projects exist
  // and what shape are they in" with all-time budget figures.
  const filteredProjects = data.allProjects.filter((project) => {
    if (statusFilter !== 'all' && project.status !== statusFilter) return false;
    if (riskOnly && !isAtRisk(project)) return false;
    if (clientFilter !== 'all' && project.clientId !== clientFilter)
      return false;
    if (personFilter !== 'all' && !projectPeopleIds(project).has(personFilter))
      return false;
    if (tierFilter !== 'all' && String(project.tier ?? 'none') !== tierFilter)
      return false;
    const haystack =
      `${project.code} ${project.name} ${project.clientName} ${project.groupName || ''}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });
  const projects = [...filteredProjects].sort((a, b) => {
    const left = projectSortValue(a, sort.key, data.users);
    const right = projectSortValue(b, sort.key, data.users);
    const result =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : collator.compare(String(left), String(right));
    return sort.direction === 'asc' ? result : -result;
  });

  function sortBy(key) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
    setFlashToken((n) => n + 1);
  }

  function renderProjectRow(project) {
    const projectHasBudget = hasBudget(project);
    const pct = projectHasBudget
      ? Math.min(100, (project.spentMD / project.budgetMD) * 100)
      : 0;
    const managers = (project.managerIds || [])
      .map((id) => data.users.find((user) => user.id === id))
      .filter(Boolean);
    const { key, className } = flashRowProps(
      project.id,
      flashToken,
      `clickable${project.status !== 'active' ? ' row-muted' : ''}`,
    );
    return (
      <tr key={key} className={className} onClick={() => onOpen(project.id)}>
        <td>
          <div className="project-cell">
            <ProjectLabel project={project} />
            <span>{project.clientName}</span>
          </div>
        </td>
        <td>
          <span className="truncate project-group-preview">
            {project.groupName || '-'}
          </span>
        </td>
        <td>
          {project.tier ? (
            `Tier ${project.tier}`
          ) : (
            <span className="muted-text">-</span>
          )}
        </td>
        <td>
          <StatusBadge status={project.status} />
        </td>
        <td className="strong">
          {projectHasBudget ? (
            `${project.budgetMD} MD`
          ) : (
            <span className="muted-text">No budget</span>
          )}
        </td>
        <td className="strong">{project.spentMD.toFixed(1)} MD</td>
        <td className="strong">
          {projectHasBudget ? (
            `${(project.budgetMD - project.spentMD).toFixed(1)} MD`
          ) : (
            <span className="muted-text">N/A</span>
          )}
        </td>
        <td>
          {projectHasBudget ? (
            <Progress pct={pct} danger={pct > 95} warn={pct > 85} />
          ) : (
            <span className="muted-text">No budget</span>
          )}
        </td>
        <td>
          {managers.map((manager) => (
            <Avatar key={manager.id} user={manager} />
          ))}
        </td>
      </tr>
    );
  }

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle={`${projects.length} project${projects.length !== 1 ? 's' : ''} · budgets in MD`}
        actions={
          <button className="primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New project
          </button>
        }
      />
      <ListToolbar
        search={search}
        onSearch={setFilter('search')}
        placeholder="Search projects"
        filters={
          <div className="toolbar-filters project-toolbar-filters">
            <div className="segmented inline">
              {[
                ['active', 'Active'],
                ['paused', 'Paused'],
                ['completed', 'Completed'],
                ['all', 'All'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={statusFilter === key ? 'active' : ''}
                  onClick={() => setFilter('status')(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="segmented inline">
              {[
                [false, 'All'],
                [true, 'At risk'],
              ].map(([key, label]) => (
                <button
                  key={String(key)}
                  className={riskOnly === key ? 'active' : ''}
                  onClick={() => setFilter('risk')(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <Select
              value={clientFilter}
              onChange={setFilter('client')}
              ariaLabel="Filter by client"
              active={clientFilter !== 'all'}
              searchable
              keepOnSearch={ALL_ONLY}
              options={[
                { value: 'all', label: 'All clients' },
                ...clientOptions.map((client) => ({
                  value: client.id,
                  label: client.name,
                })),
              ]}
            />
            <Select
              value={personFilter}
              onChange={setFilter('person')}
              ariaLabel="Filter by team member"
              active={personFilter !== 'all'}
              searchable
              keepOnSearch={ALL_ONLY}
              options={[
                { value: 'all', label: 'All team members' },
                ...personOptions.map((user) => ({
                  value: user.id,
                  label: user.name,
                })),
              ]}
            />
            <Select
              value={tierFilter}
              onChange={setFilter('tier')}
              ariaLabel="Filter by tier"
              active={tierFilter !== 'all'}
              options={TIER_FILTER_OPTIONS}
            />
            <ResetFiltersButton
              count={activeCount}
              onReset={() => setFilters(PROJECT_FILTERS)}
            />
          </div>
        }
      />
      <Table headers={PROJECT_TABLE_HEADERS} sort={sort} onSort={sortBy}>
        {projects.map(renderProjectRow)}
      </Table>
      {showCreate && (
        <ProjectModal
          data={data}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await onCreated();
          }}
        />
      )}
    </div>
  );
}

export function ProjectDetail({
  projectId,
  data,
  reload,
  tab: urlTab,
  onTabChange: setTab,
  onBack,
}) {
  const project = data.allProjects.find((item) => item.id === projectId);
  const tab = urlTab || DETAIL_TABS.projects[0];
  const {
    detail,
    loading: detailLoading,
    loaded: detailLoaded,
    error: detailError,
    reload: reloadDetail,
  } = useProjectDetail(projectId);

  // Payment milestones are editable from both the Info and Memory tabs and
  // both send a full replacement, so the save lives here — one in-flight lock
  // shared across the tabs stops a stale replacement from a tab the user
  // switched to mid-save.
  const [savingPayments, setSavingPayments] = useState(false);
  const savePayments = useCallback(
    async (items) => {
      setSavingPayments(true);
      try {
        await api(`/projects/${projectId}/payment-milestones`, {
          method: 'PUT',
          body: JSON.stringify({ items }),
        });
        await reloadDetail();
      } finally {
        setSavingPayments(false);
      }
    },
    [projectId, reloadDetail],
  );

  // Project start / planned-end dates are editable from the Info Timeline group
  // (full PUT) and the Memory timeline anchors (PATCH). One lock shared across
  // the tabs keeps the two writers from overwriting each other.
  const [savingSchedule, setSavingSchedule] = useState(false);
  const lockSchedule = useCallback(async (fn) => {
    setSavingSchedule(true);
    try {
      await fn();
    } finally {
      setSavingSchedule(false);
    }
  }, []);
  const saveSchedule = useCallback(
    (patch) =>
      lockSchedule(async () => {
        await api(`/projects/${projectId}/schedule`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
        await reload();
      }),
    [lockSchedule, projectId, reload],
  );

  // Which profile group (if any) is mid-edit/save. Lives here rather than
  // inside ProjectInfoTab: that tab unmounts whenever the user switches to
  // another tab and back, and a component-local lock would reset on remount
  // while the original group's save is still in flight — letting a second
  // group submit its own full-project snapshot and silently revert the
  // first. Hoisting it here keeps the lock alive across that remount.
  const [editingProfileGroup, setEditingProfileGroup] = useState(null);

  const next = nextMilestone(
    detail.memory.lifecycleMilestones,
    detail.paymentMilestones,
  );
  const headline = next
    ? `Next: ${next.title}`
    : STATUS_LABEL[project.status] || project.status;

  return (
    <div>
      <button className="secondary detail-back" onClick={onBack}>
        <ChevronLeft size={14} /> All projects
      </button>
      <div className="detail-head">
        <Code project={project} big />
        <div className="detail-head-title">
          <h1>{project.name}</h1>
          <p>
            {project.clientName} · {headline} ·{' '}
            {project.startDate || 'No start'} to {project.endDate || 'Open end'}
          </p>
        </div>
      </div>
      <nav className="tabs project-detail-tabs">
        {DETAIL_TABS.projects.map((key) => (
          <button
            key={key}
            className={tab === key ? 'active' : ''}
            onClick={() => setTab(key)}
          >
            {PROJECT_TAB_LABELS[key]}
          </button>
        ))}
      </nav>

      {tab === 'tracker' && <ProjectTrackerTab project={project} data={data} />}

      {tab !== 'tracker' &&
        // A failed refresh after the first load leaves `detail` stale, so the
        // editors must not stay live against it — show the retry fallback
        // until a fetch succeeds again.
        (detailLoaded && !detailError ? (
          <>
            {tab === 'info' && (
              <ProjectInfoTab
                project={project}
                data={data}
                detail={detail}
                reload={reload}
                reloadDetail={reloadDetail}
                savePayments={savePayments}
                savingPayments={savingPayments}
                lockSchedule={lockSchedule}
                savingSchedule={savingSchedule}
                editingGroup={editingProfileGroup}
                setEditingGroup={setEditingProfileGroup}
              />
            )}
            {tab === 'history' && (
              <ProjectHistoryTab
                project={project}
                detail={detail}
                loading={detailLoading}
                reloadDetail={reloadDetail}
              />
            )}
            {tab === 'memory' && (
              <ProjectMemoryTab
                project={project}
                detail={detail}
                reloadDetail={reloadDetail}
                savePayments={savePayments}
                savingPayments={savingPayments}
                saveSchedule={saveSchedule}
                savingSchedule={savingSchedule}
              />
            )}
          </>
        ) : (
          <DetailFallback
            error={detailError}
            onRetry={() => reloadDetail().catch(() => {})}
          />
        ))}
    </div>
  );
}

function DetailFallback({ error, onRetry }) {
  return (
    <div className="empty-panel">
      {error ? (
        <>
          Couldn’t load the details for this record.{' '}
          <button type="button" className="secondary small" onClick={onRetry}>
            Retry
          </button>
        </>
      ) : (
        'Loading…'
      )}
    </div>
  );
}
